'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Project {
  id: string;
  title: string;
  category: string;
  location: string;
  client: string;
  completionYear: number;
  description: string;
  image: string;
  alt: string;
  specifications: string[];
  testimonial: {
    quote: string;
    author: string;
    position: string;
  };
}

const ProjectsPortfolio = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = ['all', 'infrastructure', 'healthcare', 'industrial', 'commercial'];

  const projects: Project[] = [
  {
    id: 'amahoro-stadium',
    title: 'Amahoro Stadium Renovation',
    category: 'infrastructure',
    location: 'Kigali, Rwanda',
    client: 'Government of Rwanda',
    completionYear: 2022,
    description: 'Complete electrical infrastructure upgrade for Rwanda\'s premier sports facility, including power distribution, lighting systems, and backup generators',
    image: "https://images.unsplash.com/photo-1704908328704-784e90cfba25",
    alt: 'Modern sports stadium with floodlights illuminated at dusk showing renovated electrical infrastructure and seating areas',
    specifications: [
    '2.5 MW power distribution system',
    'LED floodlighting with smart controls',
    'Redundant backup power systems',
    'Building management system integration'],

    testimonial: {
      quote: 'New Capital delivered exceptional quality on schedule, transforming our stadium into a world-class facility.',
      author: 'Jean-Pierre Mugabo',
      position: 'Project Director, Ministry of Sports'
    }
  },
  {
    id: 'bugesera-airport',
    title: 'Bugesera International Airport',
    category: 'infrastructure',
    location: 'Bugesera, Rwanda',
    client: 'Rwanda Airports Company',
    completionYear: 2026,
    description: 'Mission-critical power distribution, backup systems, and comprehensive MEP material supply for Rwanda\'s new international gateway (2025-2026)',
    image: "https://images.unsplash.com/photo-1713809396925-4ed53f646576",
    alt: 'Modern airport terminal building at sunset with glass facade and visible electrical infrastructure including runway lighting systems',
    specifications: [
    '10 MW primary power distribution',
    'N+1 redundant UPS systems',
    'Complete MEP material provision',
    'Airfield ground lighting systems',
    'Emergency power for critical operations'],

    testimonial: {
      quote: 'Their expertise in mission-critical systems and comprehensive MEP material supply gave us confidence throughout this complex project.',
      author: 'Sarah Mukeshimana',
      position: 'Technical Manager, Rwanda Airports'
    }
  },
  {
    id: 'kemri-labs',
    title: 'KEMRI Research Laboratories',
    category: 'healthcare',
    location: 'Nairobi, Kenya',
    client: 'Kenya Medical Research Institute',
    completionYear: 2021,
    description: 'Precision electrical systems for advanced medical research facilities requiring uninterrupted power and environmental controls',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14d981296-1764840853144.png",
    alt: 'Modern medical research laboratory with advanced equipment, clean room facilities, and sophisticated electrical control systems',
    specifications: [
    'Medical-grade power quality systems',
    'Precision HVAC controls',
    'Laboratory equipment integration',
    'Compliance with international standards'],

    testimonial: {
      quote: 'The attention to detail and understanding of medical facility requirements was outstanding.',
      author: 'Dr. James Kimani',
      position: 'Director of Operations, KEMRI'
    }
  },
  {
    id: 'industrial-park',
    title: 'Kigali Special Economic Zone',
    category: 'industrial',
    location: 'Kigali, Rwanda',
    client: 'Rwanda Development Board',
    completionYear: 2023,
    description: 'Comprehensive EPC delivery for industrial park electrical infrastructure supporting manufacturing operations',
    image: "https://images.unsplash.com/photo-1717475769536-ba7cce01dbd5",
    alt: 'Modern industrial park with multiple factory buildings, electrical substations, and power distribution infrastructure at twilight',
    specifications: [
    '15 MW industrial power distribution',
    'Multiple tenant metering systems',
    'Three-phase power for heavy machinery',
    'Scalable infrastructure for expansion'],

    testimonial: {
      quote: 'New Capital\'s industrial expertise enabled rapid deployment and reliable operations from day one.',
      author: 'Emmanuel Hategeka',
      position: 'Infrastructure Manager, RDB'
    }
  }];


  const filteredProjects = selectedCategory === 'all' ?
  projects :
  projects.filter((p) => p.category === selectedCategory);

  const openProjectDetails = (project: Project) => {
    if (!isHydrated) return;
    setSelectedProject(project);
    document.body.style.overflow = 'hidden';
  };

  const closeProjectDetails = () => {
    if (!isHydrated) return;
    setSelectedProject(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <section id="projects" className="py-20 bg-surface">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-headline text-4xl md:text-5xl font-bold text-primary mb-4">
              Major Projects Portfolio
            </h2>
            <p className="font-body text-xl text-muted-foreground max-w-3xl mx-auto">
              Proven excellence in delivering mission-critical infrastructure across East Africa
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) =>
            <button
              key={category}
              onClick={() => isHydrated && setSelectedCategory(category)}
              className={`px-6 py-3 rounded-lg font-cta font-semibold transition-all duration-250 capitalize ${
              selectedCategory === category ?
              'bg-primary text-white shadow-lg' :
              'bg-card text-foreground hover:bg-muted'}`
              }>

                {category}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredProjects.map((project) =>
            <div
              key={project.id}
              className="bg-card rounded-lg overflow-hidden shadow-card hover:shadow-xl transition-all duration-250 transform hover:-translate-y-2 cursor-pointer"
              onClick={() => openProjectDetails(project)}>

                <div className="relative h-64 overflow-hidden">
                  <AppImage
                  src={project.image}
                  alt={project.alt}
                  className="w-full h-full object-cover transition-transform duration-250 hover:scale-110" />

                  <div className="absolute top-4 right-4 bg-accent text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {project.completionYear}
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="MapPinIcon" size={16} variant="solid" className="text-accent" />
                    <span className="font-body text-sm text-muted-foreground">{project.location}</span>
                  </div>

                  <h3 className="font-headline text-2xl font-bold text-primary mb-2">{project.title}</h3>
                  <p className="font-body text-muted-foreground mb-4 line-clamp-2">{project.description}</p>

                  <div className="flex items-center justify-between">
                    <span className="font-body text-sm text-muted-foreground">Client: {project.client}</span>
                    <Icon name="ArrowRightIcon" size={20} variant="outline" className="text-primary" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isHydrated && selectedProject &&
      <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
              <h3 className="font-headline text-2xl font-bold text-primary">{selectedProject.title}</h3>
              <button
              onClick={closeProjectDetails}
              className="p-2 hover:bg-muted rounded-lg transition-colors duration-250">

                <Icon name="XMarkIcon" size={24} variant="outline" className="text-foreground" />
              </button>
            </div>

            <div className="p-6">
              <div className="relative h-96 rounded-lg overflow-hidden mb-6">
                <AppImage
                src={selectedProject.image}
                alt={selectedProject.alt}
                className="w-full h-full object-cover" />

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-headline text-lg font-semibold text-primary mb-4">Project Details</h4>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Icon name="MapPinIcon" size={20} variant="solid" className="text-accent" />
                      <span className="font-body text-muted-foreground">{selectedProject.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="BuildingOfficeIcon" size={20} variant="solid" className="text-accent" />
                      <span className="font-body text-muted-foreground">{selectedProject.client}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="CalendarIcon" size={20} variant="solid" className="text-accent" />
                      <span className="font-body text-muted-foreground">Completed {selectedProject.completionYear}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-headline text-lg font-semibold text-primary mb-4">Technical Specifications</h4>
                  <ul className="space-y-2">
                    {selectedProject.specifications.map((spec, index) =>
                  <li key={index} className="flex items-start gap-2">
                        <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-1 flex-shrink-0" />
                        <span className="font-body text-muted-foreground text-sm">{spec}</span>
                      </li>
                  )}
                  </ul>
                </div>
              </div>

              <div className="bg-surface rounded-lg p-6">
                <h4 className="font-headline text-lg font-semibold text-primary mb-4">Client Testimonial</h4>
                <blockquote className="font-accent text-lg text-foreground italic mb-4">
                  "{selectedProject.testimonial.quote}"
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="bg-primary rounded-full w-12 h-12 flex items-center justify-center">
                    <Icon name="UserIcon" size={24} variant="solid" className="text-white" />
                  </div>
                  <div>
                    <div className="font-body font-semibold text-foreground">{selectedProject.testimonial.author}</div>
                    <div className="font-body text-sm text-muted-foreground">{selectedProject.testimonial.position}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </section>);

};

export default ProjectsPortfolio;